/**
 * Print + Share helpers — Phase 4.4.
 *
 * Doc: tm-ai-workspace/plan/funpaper-app/12-phase-4-subphases.md §5
 *
 * Two actions, platform-aware (WhatsApp share dihapus 2026-08-19):
 *  - printWorksheet(pdfUrl)  → open native print dialog
 *  - downloadPdf(pdfUrl, name) → save PDF to device
 *
 * Web (expo-web):
 *  - print: open PDF in new window, user prints from there
 *  - download: fetch via proxy API (`GET /v1/worksheet/:id/pdf`, hindari
 *    CORS CDN) → blob → <a download> click
 *
 * Mobile (iOS / Android via expo-print + expo-sharing):
 *  - print: Print.printToFileAsync({ html: <iframe src=pdfUrl> }) → Print.printAsync({ uri })
 *  - download: Print.printToFileAsync → Sharing.shareAsync (saves to Files / other apps)
 *
 * Each successful action should call `recordPrintStatus(worksheetId, childId, status)`
 * to update the server's approved_worksheets.print_status.
 *
 * Pure helpers (buildPdfFilename) live in
 * print-helpers.ts so they can be unit-tested without native modules.
 */

import { Platform } from 'react-native';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

import { updatePrintStatus, fetchWorksheetPdfBlob, type PrintStatus as ApiPrintStatus } from './api/worksheet';
import type { PrintResult } from './print-types';
import { trackAnalyticsEvent } from './analytics';
import {
  buildPdfFilename,
  type BatchPrintItem,
  type BatchPrintResult,
  type BatchPrintAction,
} from './print-helpers';

export { buildPdfFilename, summarizeBatch } from './print-helpers';
export type { PrintResult } from './print-types';
export type { BatchPrintItem, BatchPrintResult, BatchPrintAction };

// ----------------------------------------------------------------------------
// Web helpers (expo-web only — no expo-print, no native sharing)
// ----------------------------------------------------------------------------

function safePdfUrl(value: string): string | null {
  const trimmed = value.trim();
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:'
      ? parsed.toString()
      : null;
  } catch {
    return null;
  }
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Web: open PDF in a new window so the user can print from the browser.
 * Returns success even if user closes the window without printing — the
 * actual print is async in the browser. We still call recordPrintStatus
 * optimistically; the next page-load will refetch and show the badge.
 */
async function printWeb(pdfUrl: string): Promise<PrintResult> {
  try {
    if (typeof window === 'undefined') {
      return { ok: false, error: 'Browser only.', code: 'NoWindow' };
    }
    const opened = window.open(pdfUrl, '_blank', 'noopener,noreferrer');
    if (!opened) {
      return {
        ok: false,
        error: 'Popup diblokir. Izinkan popup untuk mencetak.',
        code: 'PopupBlocked',
      };
    }
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Gagal membuka jendela cetak.',
    };
  }
}

/**
 * Web: fetch the PDF lewat proxy API (CDN tidak mengirim header CORS, jadi
 * fetch langsung dari browser selalu gagal — 2026-08-19), lalu trigger
 * download via hidden anchor click. Returns success when the browser
 * starts the download.
 */
async function downloadPdfWeb(worksheetId: number, filename: string): Promise<PrintResult> {
  try {
    if (typeof window === 'undefined') {
      return { ok: false, error: 'Browser only.', code: 'NoWindow' };
    }
    const blob = await fetchWorksheetPdfBlob(worksheetId);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Free the blob URL after a short delay (some browsers need the URL alive).
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Gagal mengunduh PDF.',
    };
  }
}

// ----------------------------------------------------------------------------
// Mobile helpers (iOS + Android via expo-print + expo-sharing)
// ----------------------------------------------------------------------------

/**
 * Mobile: render the PDF in an iframe HTML, print to a local file URI,
 * then open the native print dialog. expo-print's printAsync accepts
 * either HTML or a PDF URI; we go via the HTML wrapper because it
 * handles the iframe loading more reliably on iOS.
 */
async function printMobile(pdfUrl: string): Promise<PrintResult> {
  try {
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body, html { margin: 0; padding: 0; height: 100%; }
  iframe { width: 100%; height: 100%; border: 0; }
</style></head><body>
<iframe src="${escapeHtmlAttribute(pdfUrl)}"></iframe>
</body></html>`;
    const { uri } = await Print.printToFileAsync({ html });
    await Print.printAsync({ uri });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Gagal membuka dialog cetak.',
    };
  }
}

/**
 * Mobile: render PDF to a local file, then open the native share sheet
 * via expo-sharing. The user can pick "Save to Files" (iOS) or "Save to
 * Downloads" (Android), or any other app that accepts PDFs.
 */
async function downloadPdfMobile(pdfUrl: string, filename: string): Promise<PrintResult> {
  try {
    const isAvailable = await Sharing.isAvailableAsync();
    if (!isAvailable) {
      return {
        ok: false,
        error: 'Berbagi file tidak tersedia di perangkat ini.',
        code: 'SharingUnavailable',
      };
    }
    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
  body, html { margin: 0; padding: 0; height: 100%; }
  iframe { width: 100%; height: 100%; border: 0; }
</style></head><body>
<iframe src="${escapeHtmlAttribute(pdfUrl)}"></iframe>
</body></html>`;
    const { uri } = await Print.printToFileAsync({ html });
    await Sharing.shareAsync(uri, {
      mimeType: 'application/pdf',
      UTI: 'com.adobe.pdf',
      dialogTitle: `Simpan ${filename}`,
    });
    return { ok: true };
  } catch (err) {
    return {
      ok: false,
      error: err instanceof Error ? err.message : 'Gagal menyimpan PDF.',
    };
  }
}

// ----------------------------------------------------------------------------
// Public API (platform-aware)
// ----------------------------------------------------------------------------

/**
 * Open the native print dialog for a worksheet.
 * Web: opens PDF in new window. Mobile: expo-print.
 */
export async function printWorksheet(pdfUrl: string | null): Promise<PrintResult> {
  if (!pdfUrl) {
    return { ok: false, error: 'PDF tidak tersedia untuk worksheet ini.' };
  }
  const safeUrl = safePdfUrl(pdfUrl);
  if (!safeUrl) {
    return { ok: false, error: 'URL PDF tidak valid.' };
  }
  trackAnalyticsEvent('Worksheet Print Initiated', { platform: Platform.OS });
  return Platform.OS === 'web' ? printWeb(safeUrl) : printMobile(safeUrl);
}

/**
 * Save the worksheet PDF to the device.
 * Web: fetch via proxy API (hindari CORS CDN) → browser download.
 * Mobile: opens share sheet (Save to Files) — native fetch tidak kena CORS,
 * jadi tetap langsung ke CDN.
 */
export async function downloadPdf(
  worksheetId: number,
  pdfUrl: string | null,
  filename: string,
): Promise<PrintResult> {
  if (Platform.OS === 'web') {
    return downloadPdfWeb(worksheetId, filename);
  }
  if (!pdfUrl) {
    return { ok: false, error: 'PDF tidak tersedia untuk worksheet ini.' };
  }
  const safeUrl = safePdfUrl(pdfUrl);
  if (!safeUrl) {
    return { ok: false, error: 'URL PDF tidak valid.' };
  }
  return downloadPdfMobile(safeUrl, filename);
}

/**
 * Update the server's approved_worksheets.print_status after a successful
 * print/PDF action. Best-effort — failure to update doesn't block the
 * user, so we just log and return void.
 */
export async function recordPrintStatus(
  worksheetId: number,
  childId: number,
  status: ApiPrintStatus,
): Promise<void> {
  try {
    await updatePrintStatus(worksheetId, childId, status);
  } catch (err) {
    // Swallow — local print/share already succeeded.
    if (__DEV__) {
      console.warn('Failed to record print status', err);
    }
  }
}

// ----------------------------------------------------------------------------
// Batch print (Phase 4.5 — "Cetak Semua")
// ----------------------------------------------------------------------------

/**
 * Sequentially run print/download for a list of worksheets.
 * Y1 limit: max 5 worksheets (per plan §6.5).
 *
 * Each item is processed one at a time. On the web, "print" opens
 * multiple popup windows; the user is expected to dismiss them as
 * they appear. On mobile, the print dialog handles the queue natively.
 */
export async function printBatch(
  items: BatchPrintItem[],
  action: BatchPrintAction,
  childId: number,
  onProgress?: (current: number, total: number) => void,
): Promise<BatchPrintResult> {
  const result: BatchPrintResult = {
    total: items.length,
    printed: 0,
    skipped: 0,
    errors: [],
  };

  for (let i = 0; i < items.length; i++) {
    const item = items[i]!;
    onProgress?.(i + 1, items.length);

    const r: PrintResult =
      action === 'print'
        ? await printWorksheet(item.pdfUrl)
        : await downloadPdf(item.id, item.pdfUrl, buildPdfFilename(item.name, item.id));

    if (r.ok) {
      result.printed++;
      const status: ApiPrintStatus = action === 'print' ? 'printed' : 'shared_pdf';
      await recordPrintStatus(item.id, childId, status);
    } else {
      result.errors.push({ id: item.id, name: item.name, error: r.error });
    }
  }

  return result;
}
